// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/SourcesPanel.test.tsx
import { NotePencil } from "@phosphor-icons/react";
import { Button, Flex, Popover, Text, TextArea } from "@radix-ui/themes";
import { useId, useState } from "react";

const NOTE_MAX_LENGTH = 4000;

/**
 * Human usage note editor for a knowledge source ("cite this only for EU
 * packaging", "superseded by the 2026 deck — reference only").
 *
 * The note is the human counterpart to the AI-extracted `description`: both
 * travel with a rule's source into the retrieval prompt, so what an admin types
 * here is what the assistant reads when it decides whether a matched rule
 * actually applies. Saving blank clears the note (server stores `null`).
 */
export function SourceNoteEditPopover({
  fileName,
  note,
  onSave,
  isSaving = false,
  sourceId,
}: {
  fileName: string;
  note: string | null | undefined;
  onSave: (note: string | null) => void;
  isSaving?: boolean;
  sourceId: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(note ?? "");
  const noteId = useId();
  const hasNote = Boolean(note && note.trim());

  function handleOpenChange(next: boolean) {
    // Re-prefill from the latest saved note on every open, so a cancelled edit
    // never leaks into the next one.
    if (next) setDraft(note ?? "");
    setOpen(next);
  }

  function handleSave() {
    const trimmed = draft.trim();
    onSave(trimmed ? trimmed : null);
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <button
          type="button"
          aria-label={
            hasNote ? `Edit note for ${fileName}` : `Add a note for ${fileName}`
          }
          className="max-w-56 cursor-pointer border-none bg-transparent p-0 text-left"
          data-testid={`knowledge-sources-note-edit-${sourceId}`}
        >
          {hasNote ? (
            <Text
              as="span"
              size="1"
              color="gray"
              className="line-clamp-2"
              data-testid={`knowledge-sources-note-${sourceId}`}
            >
              {note}
            </Text>
          ) : (
            <Text
              as="span"
              size="1"
              color="blue"
              data-testid={`knowledge-sources-note-empty-${sourceId}`}
            >
              <NotePencil aria-hidden="true" className="mr-1 inline" />
              Add note
            </Text>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Content
        className="max-h-[min(70vh,420px)] w-80 overflow-y-auto"
        aria-label={`Usage note for ${fileName}`}
        data-testid="knowledge-source-note-popover"
      >
        <Flex direction="column" gap="2">
          <label htmlFor={noteId}>
            <Text size="1" color="gray">
              When should Puntt use this document? The assistant reads this note
              alongside the AI summary when it cites rules from this source.
            </Text>
          </label>
          <TextArea
            id={noteId}
            size="1"
            rows={5}
            maxLength={NOTE_MAX_LENGTH}
            value={draft}
            placeholder="e.g. Use for EU packaging only — US claims live in the 2026 claims sheet."
            onChange={(event) => setDraft(event.target.value)}
            data-testid="knowledge-source-note-input"
          />
          <Flex gap="2" justify="end">
            {hasNote ? (
              <Button
                size="1"
                variant="soft"
                color="gray"
                onClick={() => {
                  onSave(null);
                  setOpen(false);
                }}
                data-testid="knowledge-source-note-clear"
              >
                Clear
              </Button>
            ) : null}
            <Button
              size="1"
              onClick={handleSave}
              loading={isSaving}
              data-testid="knowledge-source-note-save"
            >
              Save note
            </Button>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}

/** Read-only note text for viewers who cannot manage the Knowledge Database. */
export function SourceNoteText({
  note,
  testId,
}: {
  note: string | null | undefined;
  testId: string;
}) {
  if (!note || !note.trim()) {
    return (
      <Text size="1" color="gray" data-testid={testId}>
        —
      </Text>
    );
  }
  return (
    <Text
      as="span"
      size="1"
      color="gray"
      className="line-clamp-2"
      data-testid={testId}
    >
      {note}
    </Text>
  );
}
